<template>

  <div>

    <form novalidate @submit.prevent="validateForm" class="ap-form">
      <template v-for="(field, alias) in fields">
        <div :key="alias" :class="'control-' + field.type" v-if="!field.show || (field.show(formValues))">

          <b-form-group
              :label="field.title"
              :invalid-feedback="'Field is required'"
              :description="field.description"
              :state="getValidationState(alias)"
          >
                    <template v-if="['text', 'number'].includes(field.type)">
                      <b-form-input v-model="formValues[alias]"
                                    :type="field.type"
                                    :disabled="sending || field.disabled"
                                    :placeholder="field.placeholder"></b-form-input>
                    </template>

                      <template v-if="field.type==='password'">
                          <b-form-group class="password-field">
                              <b-form-input v-model="formValues[alias]"
                                            :type="field.type"
                                            v-if="!showPassword.includes(alias)"
                                            :disabled="sending || field.disabled"
                                            :placeholder="field.placeholder">
                              </b-form-input>
                              <b-form-input v-model="formValues[alias]"
                                            type="text"
                                            v-else
                                            :disabled="sending || field.disabled"
                                            :placeholder="field.placeholder">
                              </b-form-input>
                              <b-icon icon="eye-fill" v-if="!showPassword.includes(alias)" @click="showPass(alias)"></b-icon>
                              <b-icon icon="eye-slash-fill" v-else @click="showPass(alias)"></b-icon>
                          </b-form-group>
                      </template>

                    <b-form-textarea v-if="field.type==='textarea'"
                                     :disabled="sending || field.disabled"
                                     :placeholder="field.placeholder"
                                     v-model="formValues[alias]"/>

                    <select-box v-if="field.type==='select'"
                                :multiple="field.multiple"
                                :disabled="sending"
                                :placeholder="field.placeholder"
                                v-model="formValues[alias]"
                                :options="field.options"/>

                    <date-picker v-if="field.type==='date'"
                                 valueType="format" type="format"
                                 format="YYYY-MM-DD"
                                 :disabled="sending"
                                 @clear="formValues[alias]=0"
                                 :placeholder="field.placeholder"
                                 v-model="formValues[alias]"/>

                    <b-form-checkbox v-if="field.type==='checkbox'"
                                     :disabled="sending"
                                     v-model="formValues[alias]"
                                     value="1"
                                     unchecked-value="0"/>

                    <div class="checkbox-group" v-if="field.type==='checkbox-group'">
                        <div class="info-message" v-if="field.info" v-html="field.info"></div>
                        <b-form-checkbox v-for="(opt, i) in field.options"
                                         :key="i"
                                         :disabled="sending || field.disabled"
                                         v-model="formValues[alias][opt.id]"
                                         value="1"
                                         @change="$emit('checkboxCheck', formValues)"
                                         unchecked-value="0">{{opt.title}}</b-form-checkbox>
                    </div>

          </b-form-group>
        </div>

      </template>

      <div class="buttons">
        <template v-for="(but, alias) in buttons">
                    <span :key="alias">
                        <template v-if="alias=='close'">
                            <b-button type="button" class="md-transparent bt-close"
                                      @click="$emit('close')">{{ $t('btClose') }}</b-button>
                        </template>
                        <template v-else-if="alias === 'custom'">
                            <b-button type="button"
                                      class="md-transparent bt-close"
                                      @click="$emit('customBtnClick', formValues)">
                                {{but.title || $t('btClose')}}
                            </b-button>
                        </template>
                        <template v-else>
                         <b-button :type="but.type" :class="'md-primary mr-1 ' + but.class + ' bt-' + alias"
                                   :variant="but.variant || ''"
                                   @click="buttonClick(but, alias)" :disabled="sending">{{ but.title }}</b-button>
                        </template>
                    </span>
        </template>
      </div>
    </form>
  </div>

</template>

<script>
import Vue from 'vue'
import {validationMixin} from 'vuelidate'
import {
    sameAs,
    minLength
    //required,
} from 'vuelidate/lib/validators'
import SelectBox from "./SelectBox";
import DatePicker from 'vue2-datepicker';

// Custom validator
const required = (value) => {
    return value && value instanceof Object ? Object.values(value).length > 0 : (value ? true : false);
}

export default {
    props: ["fields", "values", "buttons"],
    components: {SelectBox, DatePicker},
    name: 'FormValidation',
    mixins: [validationMixin],
    data() {
        return {
            formSaved: false,
            formValues: {},
            sending: false,
            lastUser: null,
            showPassword: []
        }
    },

    // Get validation rules
    validations() {
        return {
            form: this.rules
        }
    },

    // Component mounted
    created() {
        // Set original values to the form
        for (let val in this.values) {
            // Skip non property
            // eslint-disable-next-line no-prototype-builtins
            if (!this.values.hasOwnProperty(val)) continue;

            // Get value
            let vValue = this.values[val];

            // Check if value object
            // Unpack object vals to obj:key
            if (vValue && typeof vValue === 'object') {
                for(const key of Object.keys(vValue)) {
                    Vue.set(this.formValues, val+':'+key, vValue[key]);
                }
            } else {
                // Store value into the form
                Vue.set(this.formValues, val, vValue);
            }
        }

        //console.log(this.formValues);

        // Fill form vals
        for (let field in this.fields) {
            // Skip property
            // eslint-disable-next-line no-prototype-builtins
            if (!this.fields.hasOwnProperty(field)) continue;

            // Init form fields
            if (this.values && this.values[field] !== undefined) {
                // Set value
                Vue.set(this.formValues, field, this.values[field]);
            }
        }
    },

    // Computed vars
    computed:
        {
            /**
             * Validations list
             * @returns {{}}
             */
            rules() {

                // Field
                let valids = {};

                // Create validation rules
                for (let field in this.fields) {

                    // Skip not own props
                    // eslint-disable-next-line no-prototype-builtins
                    if (!this.fields.hasOwnProperty(field)) continue;

                    // Skip not shown items
                    if (this.fields[field].show && !this.fields[field].show(this.formValues)) continue;

                    // Get field
                    let vals = this.fields[field].validation;

                    // Process validation
                    if (vals) for (let rule of vals) {
                        // Validation function
                        let vf = false;

                        // Get validatoon function
                        switch (rule) {
                            case "required":
                                vf = required;
                                break;
                            case "repeatPassword":
                                vf = sameAs('password');
                                break;

                            case "password":
                                vf = minLength(6);
                                break;
                            /*                                case "requiredMultiple":
                                                                vf = requiredMultiple;
                                                                break;*/
                        }

                        // Set validation function
                        if (vf) {
                            // Init object
                            if (!valids[field]) valids[field] = {};

                            // Set value
                            valids[field][rule] = vf;
                        }
                    }
                }

                // Return list
                return valids;
            },


            /**
             * Simulate form for validation
             */

            form() {
                return this.formValues;
            }
        },

    // Methods list
    methods: {
        showPass(alias) {
            if (this.showPassword.includes(alias)) {
                this.showPassword.splice(this.showPassword.indexOf(alias), 1);
                return;
            }

            this.showPassword.push(alias)
        },
        /**
         * Options source
         */
        optionsSource(field) {

            // Check for field config
            if (!field.options) return [];

            // Check for function
            if (typeof field.options === "function") return field.options(this.formValues);

            // Return static options
            return field.options;
        },

        /**
         * Options source
         */
        fieldText(field) {

            // Check for field config
            if (!field.text) return "";

            // Check for function
            if (typeof field.text === "function") return field.text(this.formValues);

            // Return static options
            return field.text;
        },

        /**
         * Validation state
         */
        getValidationState(fieldName) {
            const field = this.$v.form[fieldName]

            // Check for result
            if (field) {
                return !field.$invalid;
            }
        },

        /**
         * Savefvisi form
         */
        saveForm() {

            // Sending process
            this.sending = true
            this.formSaved = true
            this.sending = false

            // Rework form values
            let vals = {}
            for (const fld of Object.keys(this.formValues)) {

                // check if field is object value
                // Set complex value as object.key
                const [obj, key] = fld.split(":")
                if (obj && key) {
                    if (!vals[obj]) vals[obj] = {}
                    vals[obj][key] = this.formValues[fld]
                } else {

                    // Set simple value
                    vals[fld] = this.formValues[fld]
                }
            }

            // Emit changes
            this.$emit('change', vals);
        },

        /**
         * Button click
         */

        buttonClick(but, alias) {
            // Store submit action
            Vue.set(this.formValues, "action", alias);

            // Emit event
            if (but.emit) {
                // Emit action
                this.$emit(but.emit);
            }
        },

        /**
         * Validate form
         */
        validateForm() {

            // Validate form
            this.$v.$touch()

            // Check for validation result and submit form
            if (!this.$v.$invalid) {
                this.saveForm()
            }
        }
    }
}
</script>

<style lang="scss">

@import './src/sass/ap-form.scss';

</style>
